import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { EVENTS, track } from '@/lib/analytics';
import { saveSession } from '@/utils/sessionStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = ({ onUserLogin }) => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      // Extract session_id from URL fragment
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        toast.error('Authentication failed - no session ID');
        navigate('/onboarding?login=true');
        return;
      }

      try {
        // Exchange session_id for user data via backend
        const response = await axios.post(
          `${API}/auth/google/callback`,
          { session_id: sessionId }
        );

        const { user, session_token } = response.data;

        // Store session token in localStorage + IndexedDB
        await saveSession(session_token, user);

        // Log in user
        onUserLogin(user);

        // If the user landed here via an invite link, auto-enroll them in
        // the invited community. `via_invite=true` bypasses approval since
        // possession of the invite link is the consent.
        let joinedCommunityId = null;
        const inviteCommunityId = localStorage.getItem('inviteCommunityId');
        const inviteCommunityName = localStorage.getItem('inviteCommunityName');
        if (inviteCommunityId) {
          try {
            const inviteToken = localStorage.getItem('inviteToken');
            const joined = await axios.post(
              `${API}/memberships?user_id=${user.id}&via_invite=true${inviteToken ? `&invite_token=${encodeURIComponent(inviteToken)}` : ''}`,
              { communityId: inviteCommunityId }
            );
            joinedCommunityId = inviteCommunityId;
            if (joined?.data?.status !== 'pending') track(EVENTS.MEMBER_JOINED, { community_id: inviteCommunityId, via: 'invite' });   // P0215
            if (inviteCommunityName) toast.success(`Welcome to ${inviteCommunityName}!`);
          } catch (joinErr) {
            const detail = joinErr?.response?.data?.detail;
            if (detail === 'Already a member of this community') {
              joinedCommunityId = inviteCommunityId;
            } else {
              console.warn('Could not auto-join invite community via Google:', detail);
            }
          } finally {
            localStorage.removeItem('inviteCommunityId');
            localStorage.removeItem('inviteCommunityName');
            localStorage.removeItem('inviteInviterId');
            localStorage.removeItem('inviteToken');
          }
        }

        // Check if new user (needs onboarding)
        if (user.isNewUser) {
          toast.success('Welcome! Let\'s set up your account.');
          navigate('/onboarding', { state: { user, skipToProfile: true } });
        } else if (joinedCommunityId) {
          navigate(`/communities/${joinedCommunityId}`, { state: { user } });
        } else {
          toast.success('Welcome back!');
          navigate('/requestor', { state: { user } });
        }
      } catch (error) {
        console.error('Google auth callback error:', error);
        const detail = error.response?.data?.detail;
        // Iter 118.31 — the backend deliberately 404s with this shape
        // instead of silently creating an incomplete account (see
        // _find_or_create_google_user). This was always meant to route
        // a brand-new Google user into onboarding with their info
        // prefilled -- it was just never wired up here, so every
        // first-time Google sign-in fell into the generic error below.
        if (detail?.code === 'account_not_found') {
          const nameParts = (detail.name || '').trim().split(/\s+/).filter(Boolean);
          try {
            localStorage.setItem('pendingOAuthEmail', detail.email || '');
            localStorage.setItem('pendingOAuthName', nameParts[0] || '');
            localStorage.setItem('pendingOAuthLastName', nameParts.slice(1).join(' '));
          } catch (_) { /* localStorage unavailable -- prefill just won't happen */ }
          // No toast here -- OnboardingPage shows a persistent banner
          // instead, since this context ("why did signing in send me
          // here?") is worth more than a few seconds of visibility.
          navigate('/onboarding');
          return;
        }
        // A genuine failure (expired session, auth proxy unreachable, an
        // unexpected server error) -- not "vanish and leave them stuck":
        // persistent toast with a direct way to reach support.
        toast.error('Authentication failed. Please try again.', {
          duration: Infinity,
          action: {
            label: 'Email us',
            onClick: () => {
              window.location.href = `mailto:prayer@themustardseedlings.com?subject=${encodeURIComponent('Trouble signing in')}`;
            },
          },
        });
        navigate('/onboarding?login=true');
      }
    };

    processCallback();
  }, [navigate, onUserLogin]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#4E8B7C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#5C6666]">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
